import { authErrorHandler } from '@/services/error/authHandler.js';

/**
 * Универсальный метод для получения подключений пользователя.
 * @param {Object} params - Параметры запроса.
 * @param {string} params.userId - ID пользователя.
 * @param {string} params.type - Тип подключений(followers/following).
 * @param {number} [params.offset=0] - Смещение для пагинации.
 * @param {number} [params.limit=20] - Лимит для пагинации.
 * @returns {Promise<Object|null>} - Данные подписчиков/подписок пользователя или null в случае ошибки.
 */
export const fetchUserConnections = async (params = {}) => {
  try {
    if (!params.userId || params.userId === "null") {
      console.error("userId is invalid:", params.userId);
      return null;
    }
    if (!params.type || params.type === "null") {
      console.error("type is invalid:", params.type);
      return null;
    }

    const { userId, type, ...queryParams } = params;    
    
    const filteredParams = Object.fromEntries(
      Object.entries({
        ...queryParams,
        offset: queryParams.offset || 0,
        limit: queryParams.limit || 15
      }).filter(([_, value]) => value !== undefined)
    );

    const searchParams = new URLSearchParams(filteredParams).toString();
    const url = `/api/users/${userId}/${type}?${searchParams}`;
    
    const response = await fetch(url, {
      headers: { "Cache-Control": "no-cache" },
    });

    if (response.status === 401 || response.status === 403) {
      authErrorHandler(response.status);
      return null;
    }

    const data = await response.json();

    if (response.ok) {
      return data;
    } else {
      console.error(`Error fetching ${type}:`, data);
      return null;
    }
  } catch (error) {
    console.error(`Error during ${params.type} API call:`, error);
    return null;
  }
};

/**
 * Универсальный метод для управления подписками (follow/unfollow).
 * @param {string} userId - ID пользователя.
 * @param {string} action - Тип действия ('follow' или 'unfollow').
 * @param {boolean} [negative] - Для действия follow: является ли это "скептической" подпиской.
 * @returns {Promise<void>} - Данные пользователя на которого подписывается/отписывается или null в случае ошибки.
 */
export const manageFollowState = async (userId, action, negative = false) => {  
  const url = `/api/users/${userId}/${action}`;
  const options =
    action === "follow" ? { body: JSON.stringify({ negative }) } : {};

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: options.body || undefined,
    });

    if (response.status === 401 || response.status === 403) {
      authErrorHandler(response.status);
      return null;
    }

    const data = await response.json();

    if (response.ok) {     
      return data;
    } else {
      console.error(`Ошибка при выполнении действия "${action}":`, data);
      return null;
    }
  } catch (error) {
    console.error(`Ошибка при выполнении действия "${action}":`, error);
    throw error;
  }
};
